import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Grid, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import api from "../../api/client";
import { endpoints } from "../../api/endpoints";
import { PageHeader, SectionCard, StatCard, StatusChip, EmptyState } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";
import { EXPENSE_CATEGORIES, getCategoryAmount, lineTotal } from "../../utils/reimbursementExpenseCategories";

const money = (n) => `₹${(parseFloat(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "—");

function normalizeStatus(s) {
  const st = String(s || "pending").toLowerCase();
  return st === "disapproved" ? "rejected" : st;
}

function claimStatus(expenses = []) {
  const statuses = expenses.map((e) => normalizeStatus(e.status));
  if (!statuses.length) return "pending";
  if (statuses.every((s) => s === "paid")) return "paid";
  if (statuses.every((s) => s === "approved" || s === "paid")) return "approved";
  if (statuses.every((s) => s === "rejected")) return "rejected";
  if (statuses.some((s) => s === "rejected")) return "partial";
  return "pending";
}

function canEditOrDelete(expenses = []) {
  if (!expenses.length) return true;
  return expenses.every((e) => normalizeStatus(e.status) === "pending");
}

export default function MyReimbursementsNew() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(endpoints.reimbursement.myDetail);
      const list = Array.isArray(res.data) ? res.data : [];
      list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setRows(list);
    } catch (err) {
      toast.error(extractErrorMessage(err));
      setRows([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    let pending = 0, approved = 0, rejected = 0, paid = 0, totalPending = 0, totalPaid = 0;
    rows.forEach((r) => {
      (r.expenses || []).forEach((e) => {
        const amt = lineTotal(e);
        const st = normalizeStatus(e.status);
        if (st === "approved") { approved += 1; totalPending += amt; }
        else if (st === "rejected") rejected += 1;
        else if (st === "paid") { paid += 1; totalPaid += amt; }
        else { pending += 1; totalPending += amt; }
      });
    });
    return { pending, approved, rejected, paid, totalPending, totalPaid };
  }, [rows]);

  const handleDelete = async () => {
    if (!confirmDelete?._id) return;
    setDeletingId(confirmDelete._id);
    try {
      await api.delete(endpoints.reimbursement.remove(confirmDelete._id));
      toast.success("Reimbursement deleted");
      setConfirmDelete(null);
      setRows((prev) => prev.filter((r) => r._id !== confirmDelete._id));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box p={3}>
      <PageHeader
        title="My Reimbursements"
        subtitle="Your submitted expense claims — only your requests are shown here"
        icon={<ReceiptLongOutlinedIcon />}
        actions={[
          <Button key="new" variant="contained" startIcon={<AddIcon />} onClick={() => nav("/ReimbursementForm")}>
            New Request
          </Button>,
        ]}
      />

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <StatCard label="Pending" value={stats.pending} hint={money(stats.totalPending)} icon={<HourglassEmptyOutlinedIcon />} accent="#F9BC06" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Approved" value={stats.approved} hint="Awaiting payment" icon={<CheckCircleOutlineIcon />} accent="#39C645" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Paid" value={stats.paid} hint={money(stats.totalPaid)} icon={<PaidOutlinedIcon />} accent="#6C50BF" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Rejected" value={stats.rejected} hint="Need to resubmit" icon={<CancelOutlinedIcon />} accent="#FF6B6B" />
        </Grid>
      </Grid>

      <SectionCard title="My Claims" subtitle={`${rows.length} request(s) — newest first`}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<ReceiptLongOutlinedIcon />}
            title="No reimbursements yet"
            description="Submit your first expense claim to get started."
            cta={<Button variant="contained" startIcon={<AddIcon />} onClick={() => nav("/ReimbursementForm")}>New Request</Button>}
          />
        ) : (
          <Stack spacing={2}>
            {rows.map((r, idx) => {
              const isOpen = !!expanded[r._id];
              const totalAmt = (r.expenses || []).reduce((s, e) => s + lineTotal(e), 0);
              const overall = claimStatus(r.expenses);
              const mutable = canEditOrDelete(r.expenses);
              return (
                <Box key={r._id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden", transition: "border-color 0.2s", "&:hover": { borderColor: "primary.main" } }}>
                  <Stack
                    direction="row" alignItems="center" spacing={2}
                    onClick={() => setExpanded((e) => ({ ...e, [r._id]: !e[r._id] }))}
                    sx={{ p: 2, cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
                  >
                    <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: "#F4EDFA", color: "primary.main", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <ReceiptLongOutlinedIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                          {r.purpose || r.clientName || `Claim #${rows.length - idx}`}
                        </Typography>
                        <StatusChip status={overall} />
                      </Stack>
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                        Submitted {fmtDate(r.createdAt)}
                        {r.fromDate && r.toDate ? ` · ${fmtDate(r.fromDate)} — ${fmtDate(r.toDate)}` : ""}
                        {" · "}{r.expenses?.length || 0} item(s)
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: "primary.main" }}>{money(totalAmt)}</Typography>
                    {mutable && (
                      <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => nav(`/ReimbursementForm?id=${r._id}`)}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={deletingId === r._id}
                            onClick={() => setConfirmDelete(r)}
                          >
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                    <IconButton size="small">{isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                  </Stack>
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box sx={{ borderTop: "1px solid", borderColor: "divider", bgcolor: "background.default", p: 2 }}>
                      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap mb={2}>
                        {[
                          { label: "Employee", value: r.employeeName },
                          { label: "Manager", value: r.managerName },
                          { label: "Department", value: r.department },
                          { label: "Location", value: r.location },
                          { label: "Client", value: r.clientName },
                          { label: "Purpose", value: r.purpose },
                          { label: "Submitted on", value: r.createdAt ? new Date(r.createdAt).toLocaleString("en-IN") : null },
                        ].filter((x) => x.value).map((m) => (
                          <Box key={m.label}>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary" }}>
                              {m.label}
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{m.value}</Typography>
                          </Box>
                        ))}
                      </Stack>

                      {mutable && (
                        <Stack direction="row" spacing={1} mb={2}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditOutlinedIcon />}
                            onClick={() => nav(`/ReimbursementForm?id=${r._id}`)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteOutlineOutlinedIcon />}
                            disabled={deletingId === r._id}
                            onClick={() => setConfirmDelete(r)}
                          >
                            Delete
                          </Button>
                        </Stack>
                      )}

                      <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1, textTransform: "uppercase", color: "text.secondary" }}>
                        Expense lines
                      </Typography>
                      <Box sx={{ overflowX: "auto" }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Date</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Description</TableCell>
                              {EXPENSE_CATEGORIES.filter((c) =>
                                (r.expenses || []).some((e) => getCategoryAmount(e, c) > 0)
                              ).map((c) => (
                                <TableCell key={c.key} align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", whiteSpace: "nowrap" }}>
                                  {c.label}
                                </TableCell>
                              ))}
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Total</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Receipts</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(r.expenses || []).map((exp) => {
                              const activeCats = EXPENSE_CATEGORIES.filter((c) =>
                                (r.expenses || []).some((e) => getCategoryAmount(e, c) > 0)
                              );
                              return (
                                <TableRow key={exp._id}>
                                  <TableCell sx={{ fontSize: 13 }}>{fmtDate(exp.date)}</TableCell>
                                  <TableCell sx={{ fontSize: 13 }}>
                                    {exp.description || "—"}
                                    {exp.note && <Typography sx={{ fontSize: 11, color: "text.secondary", fontStyle: "italic" }}>{exp.note}</Typography>}
                                  </TableCell>
                                  {activeCats.map((c) => (
                                    <TableCell key={c.key} align="right" sx={{ fontSize: 13 }}>
                                      {getCategoryAmount(exp, c) > 0 ? money(getCategoryAmount(exp, c)) : "—"}
                                    </TableCell>
                                  ))}
                                  <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700 }}>{money(lineTotal(exp))}</TableCell>
                                  <TableCell>
                                    <StatusChip status={normalizeStatus(exp.status)} />
                                    {exp.remark && <Typography sx={{ fontSize: 10, color: "text.secondary", mt: 0.5, fontStyle: "italic" }}>{exp.remark}</Typography>}
                                  </TableCell>
                                  <TableCell>
                                    <Stack spacing={0.5}>
                                      {(exp.files || []).map((f, i) => {
                                        const isPdf = String(f).toLowerCase().endsWith(".pdf");
                                        return (
                                          <Stack key={i} direction="row" spacing={0.5} alignItems="center" component="a" href={f} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 12, textDecoration: "none", color: "primary.main", "&:hover": { textDecoration: "underline" } }}>
                                            {isPdf ? <PictureAsPdfOutlinedIcon fontSize="small" /> : <InsertDriveFileOutlinedIcon fontSize="small" />}
                                            <span>File {i + 1}</span>
                                          </Stack>
                                        );
                                      })}
                                      {!exp.files?.length && <Typography sx={{ fontSize: 12, color: "text.secondary" }}>—</Typography>}
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Stack>
        )}
      </SectionCard>

      <Dialog open={!!confirmDelete} onClose={() => !deletingId && setConfirmDelete(null)}>
        <DialogTitle>Delete reimbursement?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently remove
            {confirmDelete?.purpose ? ` “${confirmDelete.purpose}”` : " this claim"}
            . Only pending claims can be deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={!!deletingId}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={!!deletingId}>
            {deletingId ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
